import React, { ChangeEvent, useMemo, useState } from "react";
import {
  Form,
  Input,
  Button,
  Tabs,
  Popconfirm,
  Typography,
  Select,
  Modal,
  Radio,
} from "antd";
import { PlusOutlined } from "@ant-design/icons";
import {
  CourseEvaluation,
  RelevantCourseItem,
} from "../../../../services/courses/courses.types";
import { CourseMinimalResponse } from "../../../../types/api/getCourse";
import { LessonMinimalResponse } from "../../../../types/api/getLesson";
import { ProgramEntity } from "../../../../services/programs/programs.types";
import CourseQuestionsForm from "./CourseQuestionsForm";
import CourseLessonsSelector from "./CourseLessonsSelector";

export type DiagnosticEvaluationMode = "rubricas" | "preguntas";

interface LessonSelected {
  id: number;
  title: string;
  _uid?: string;
}

const { TextArea } = Input;
const { Title } = Typography;

interface DiagnosticCourseFormProps {
  courseEvaluations: CourseEvaluation[];
  setCourseEvaluations: React.Dispatch<
    React.SetStateAction<CourseEvaluation[]>
  >;
  handleAddEvaluation: () => void;
  handleRemoveEvaluation: (index: number) => void;
  handleEvaluationChange: (
    index: number,
    field: keyof CourseEvaluation,
    value: string,
  ) => void;
  checkEvaluationContent: (index: number) => boolean;
  evaluationMode: DiagnosticEvaluationMode;
  onEvaluationModeChange: (mode: DiagnosticEvaluationMode) => void;
  // Axis catalog for the course's subject, consumed by the questions mode
  // (SCRUM-740). Empty hides the per-question axis selector.
  availableAxes: string[];

  selectedLessons: LessonSelected[];
  availableLessons: LessonMinimalResponse[];
  handleRemoveLesson: (index: number) => void;
  handleAddLessonAtIndex: (index: number) => void;
  handleLessonChange: (index: number, field: string, value: any) => void;
  handleReorderLessons: (lessons: LessonSelected[]) => void;

  allCourses: CourseMinimalResponse[];
  relevantCourses: RelevantCourseItem[];
  setRelevantCourses: (items: RelevantCourseItem[]) => void;
  programs: ProgramEntity[];
  currentCourseId?: number;
}

const DiagnosticCourseForm: React.FC<DiagnosticCourseFormProps> = ({
  courseEvaluations,
  setCourseEvaluations,
  handleAddEvaluation,
  handleRemoveEvaluation,
  handleEvaluationChange,
  checkEvaluationContent,
  evaluationMode,
  onEvaluationModeChange,
  availableAxes,
  selectedLessons,
  availableLessons,
  handleRemoveLesson,
  handleAddLessonAtIndex,
  handleLessonChange,
  handleReorderLessons,
  allCourses,
  relevantCourses,
  setRelevantCourses,
  programs,
  currentCourseId,
}) => {
  const [courseToAdd, setCourseToAdd] = useState<number | undefined>(undefined);
  const [programToAdd, setProgramToAdd] = useState<number | undefined>(
    undefined,
  );
  const [isRelevantModalOpen, setIsRelevantModalOpen] = useState(false);

  const selectableCourses = useMemo(() => {
    return allCourses
      .filter((c) => c.id !== currentCourseId)
      .filter((c) => !relevantCourses.some((r) => r.course_id === c.id))
      .sort((a, b) => b.id - a.id);
  }, [allCourses, currentCourseId, relevantCourses]);

  const selectedCourses = useMemo(() => {
    const byId = new Map(allCourses.map((c) => [c.id, c] as const));
    return relevantCourses.map((r) => ({
      ...r,
      course: byId.get(r.course_id) || {
        id: r.course_id,
        title: `Curso ${r.course_id}`,
      },
    }));
  }, [allCourses, relevantCourses]);

  const programsForSelectedCourse = useMemo(() => {
    if (!courseToAdd) return [];
    const course = allCourses.find((c) => c.id === courseToAdd);
    const ids = course?.program_ids ?? [];
    return programs.filter((p) => ids.includes(p.id));
  }, [courseToAdd, allCourses, programs]);

  const programById = useMemo(() => {
    return new Map(programs.map((p) => [p.id, p] as const));
  }, [programs]);

  const canAddMore = relevantCourses.length < 3;

  const handleAddRelevantCourse = () => {
    if (!canAddMore || !courseToAdd || !programToAdd) return;
    setRelevantCourses([
      ...relevantCourses,
      { course_id: courseToAdd, program_id: programToAdd },
    ]);
    setCourseToAdd(undefined);
    setProgramToAdd(undefined);
    setIsRelevantModalOpen(false);
  };

  const handleRemoveRelevantCourse = (courseId: number) => {
    setRelevantCourses(relevantCourses.filter((r) => r.course_id !== courseId));
  };

  return (
    <div>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "16px",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
          <Title level={4} style={{ marginBottom: 0 }}>
            Evaluaciones
          </Title>
          <Radio.Group
            optionType="button"
            buttonStyle="solid"
            value={evaluationMode}
            onChange={(e) =>
              onEvaluationModeChange(e.target.value as DiagnosticEvaluationMode)
            }
          >
            <Radio.Button value="rubricas">Rúbricas (Kinder)</Radio.Button>
            <Radio.Button value="preguntas">Preguntas (Primaria)</Radio.Button>
          </Radio.Group>
        </div>
        {evaluationMode === "rubricas" && (
          <Button
            type="dashed"
            onClick={handleAddEvaluation}
            icon={<PlusOutlined />}
            disabled={courseEvaluations.length >= 10}
          >
            Agregar Evaluación
          </Button>
        )}
      </div>

      {evaluationMode === "preguntas" && (
        <CourseQuestionsForm
          courseEvaluations={courseEvaluations}
          setCourseEvaluations={setCourseEvaluations}
          availableAxes={availableAxes}
        />
      )}

      {evaluationMode === "rubricas" && (
      <Tabs
        type="card"
        items={courseEvaluations.map((evaluation, index) => ({
          key: index.toString(),
          label: `${index + 1}`,
          children: (
            <div key={index}>
              <Form.Item
                label="Aprendizajes clave"
                name={["courseEvaluations", index, "key_learnings"]}
              >
                <TextArea
                  rows={3}
                  value={evaluation.key_learnings}
                  onChange={(e: ChangeEvent<HTMLTextAreaElement>) =>
                    handleEvaluationChange(
                      index,
                      "key_learnings",
                      e.target.value,
                    )
                  }
                />
              </Form.Item>

              <div className="rubricSection">
                <div className="rubricRow">
                  <div className="rubricItem">
                    <div className="rubricLabel">Rúbrica: logrado</div>
                    <Form.Item
                      name={["courseEvaluations", index, "rubric_achieved"]}
                      style={{ marginBottom: 0 }}
                    >
                      <TextArea
                        rows={4}
                        value={evaluation.rubric_achieved}
                        onChange={(e: ChangeEvent<HTMLTextAreaElement>) =>
                          handleEvaluationChange(
                            index,
                            "rubric_achieved",
                            e.target.value,
                          )
                        }
                      />
                    </Form.Item>
                  </div>
                  <div className="rubricItem">
                    <div className="rubricLabel">Rúbrica: en proceso</div>
                    <Form.Item
                      name={["courseEvaluations", index, "rubric_in_process"]}
                      style={{ marginBottom: 0 }}
                    >
                      <TextArea
                        rows={4}
                        value={evaluation.rubric_in_process}
                        onChange={(e: ChangeEvent<HTMLTextAreaElement>) =>
                          handleEvaluationChange(
                            index,
                            "rubric_in_process",
                            e.target.value,
                          )
                        }
                      />
                    </Form.Item>
                  </div>
                  <div className="rubricItem">
                    <div className="rubricLabel">Rúbrica: requiere apoyo</div>
                    <Form.Item
                      name={[
                        "courseEvaluations",
                        index,
                        "rubric_needs_support",
                      ]}
                      style={{ marginBottom: 0 }}
                    >
                      <TextArea
                        rows={4}
                        value={evaluation.rubric_needs_support}
                        onChange={(e: ChangeEvent<HTMLTextAreaElement>) =>
                          handleEvaluationChange(
                            index,
                            "rubric_needs_support",
                            e.target.value,
                          )
                        }
                      />
                    </Form.Item>
                  </div>
                </div>
              </div>

              <Form.Item
                label="Eje (Opcional)"
                name={["courseEvaluations", index, "axis"]}
              >
                <Input
                  value={evaluation.axis}
                  onChange={(e: ChangeEvent<HTMLInputElement>) =>
                    handleEvaluationChange(index, "axis", e.target.value)
                  }
                />
              </Form.Item>

              {courseEvaluations.length > 1 && (
                <Popconfirm
                  title="¿Eliminar esta evaluación?"
                  description="Esta acción no se puede deshacer"
                  onConfirm={() => handleRemoveEvaluation(index)}
                  okText="Sí"
                  cancelText="No"
                  disabled={!checkEvaluationContent(index)}
                >
                  <Button
                    danger
                    disabled={!checkEvaluationContent(index)}
                    style={{ marginTop: "8px" }}
                  >
                    Eliminar Evaluación
                  </Button>
                </Popconfirm>
              )}
            </div>
          ),
        }))}
      />
      )}

      {/* Lecciones (guía de aplicación del diagnóstico) */}
      <div style={{ marginTop: 24 }}>
        <CourseLessonsSelector
          selectedLessons={selectedLessons}
          availableLessons={availableLessons}
          handleRemoveLesson={handleRemoveLesson}
          handleAddLessonAtIndex={handleAddLessonAtIndex}
          handleLessonChange={handleLessonChange}
          handleReorderLessons={handleReorderLessons}
        />
      </div>

      {/* Cursos relevantes */}
      <div className="selectRow" style={{ marginTop: 24 }}>
        <div
          style={{
            display: "flex",
            alignItems: "baseline",
            justifyContent: "space-between",
            gap: 12,
          }}
        >
          <Title level={4} style={{ marginBottom: 0 }}>
            Cursos relevantes
          </Title>
          <Typography.Text type="secondary">
            {relevantCourses.length}/3
          </Typography.Text>
        </div>

        {selectedCourses.length > 0 ? (
          <div style={{ marginTop: 12 }}>
            {selectedCourses.map(({ course, program_id }) => {
              const program = program_id
                ? programById.get(program_id)
                : undefined;

              return (
                <div
                  key={course.id}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    padding: "12px 12px",
                    borderBottom: "1px solid var(--bo-card-border)",
                  }}
                >
                  <div
                    style={{ display: "flex", gap: 16, alignItems: "center" }}
                  >
                    <div style={{ minWidth: 260 }}>
                      {course.id} {course.title}
                    </div>
                    {program ? (
                      <div style={{ color: "#999" }}>
                        {program.program_code}
                      </div>
                    ) : null}
                  </div>

                  <Button
                    type="text"
                    danger
                    onClick={() => handleRemoveRelevantCourse(course.id)}
                  >
                    Eliminar
                  </Button>
                </div>
              );
            })}
          </div>
        ) : null}

        <div
          style={{
            marginTop: 12,
            border: "1px dashed var(--bo-border-strong)",
            borderRadius: 8,
            padding: 16,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            cursor: canAddMore ? "pointer" : "not-allowed",
            opacity: canAddMore ? 1 : 0.5,
          }}
          onClick={() => {
            if (!canAddMore) return;
            setIsRelevantModalOpen(true);
          }}
        >
          <Typography.Text strong>Agregar cursos</Typography.Text>
        </div>

        <Modal
          title={`Agregar curso relevante (${relevantCourses.length}/3)`}
          open={isRelevantModalOpen}
          onCancel={() => {
            setIsRelevantModalOpen(false);
            setCourseToAdd(undefined);
            setProgramToAdd(undefined);
          }}
          okText="Agregar"
          onOk={handleAddRelevantCourse}
          okButtonProps={{
            disabled: !canAddMore || !courseToAdd || !programToAdd,
          }}
          cancelText="Cancelar"
        >
          <Select
            showSearch
            placeholder="Buscar curso"
            value={courseToAdd}
            onChange={(value) => {
              setCourseToAdd(value);
              setProgramToAdd(undefined);
            }}
            style={{ width: "100%" }}
            optionFilterProp="label"
            disabled={!canAddMore}
            options={selectableCourses.map((c) => ({
              value: c.id,
              label: `${c.id} - ${c.title}`,
            }))}
          />
          <div style={{ marginTop: 16 }}>
            <Typography.Text strong>Programa asociado</Typography.Text>
            <Select
              placeholder="Seleccionar programa"
              value={programToAdd}
              onChange={(value) => setProgramToAdd(value)}
              style={{ width: "100%", marginTop: 8 }}
              disabled={!courseToAdd || programsForSelectedCourse.length === 0}
              options={programsForSelectedCourse.map((p) => ({
                value: p.id,
                // Programs without a code (e.g. duplicates) fall back to the
                // title so the option is never blank.
                label: p.program_code ?? p.title,
              }))}
            />
          </div>
          {!canAddMore ? (
            <Typography.Text
              type="secondary"
              style={{ display: "block", marginTop: 8 }}
            >
              Alcanzaste el máximo de 3 cursos relevantes.
            </Typography.Text>
          ) : null}
        </Modal>
      </div>
    </div>
  );
};

export default DiagnosticCourseForm;
